import { addMonths, endOfMonth, startOfMonth } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { HiOutlineAdjustmentsHorizontal } from 'react-icons/hi2';
import { useToast } from '../../../context/ToastContext';
import { useGetUsersQuery } from '../../../services/endpoints/people/user';
import { useGetAllLocationsQuery } from '../../../services/endpoints/places/location';
import { getCurrentUser } from '../../../services/helper';
import { SimpleSFCLocation } from '../../../types/place.types';
import { isAdmin, isSuper } from '../../../utils/consts';
import { dateFromString, dateInTimeZone, dateToUTC, formatDate } from '../../../utils/dateUtils';

interface FiltersProps {
  onApplyFilters: (filters: { operator: string; field: string; value: string }[]) => void;
  disabled?: boolean;
  loading?: boolean;
}

interface InstructorOption {
  id: number;
  label: string;
}

export const Filters: React.FC<FiltersProps> = ({ onApplyFilters, disabled, loading }) => {
  const currentUser = getCurrentUser();
  const isAdminUser = isAdmin(currentUser?.user_type_id!);
  const isSuperAdmin = isSuper(currentUser?.user_type_id!);
  const { data, error, isLoading } = useGetUsersQuery(
    {
      scope: 'colleagues',
      data: { entries: 10000, orderBy: { field: 'first_name', order: 'asc' } }
    },
    { skip: !isAdminUser }
  );
  const [fromDate, setFromDate] = useState<string>(
    formatDate(dateInTimeZone(startOfMonth(addMonths(new Date(), -6))), 'yyyy-MM-dd')
  );
  const [toDate, setToDate] = useState<string>(
    formatDate(dateInTimeZone(endOfMonth(addMonths(new Date(), 6))), 'yyyy-MM-dd')
  );
  const [selectedInstructor, setSelectedInstructor] = useState<InstructorOption[]>([]);

  const instructorOptions: InstructorOption[] =
    data?.results
      ?.map((instructor) => ({
        id: instructor.id,
        label:
          instructor.full_name?.trim() ||
          `${instructor.first_name ?? ''} ${instructor.last_name ?? ''}`.trim() ||
          `Instructor ${instructor.id}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const [location, setLocation] = useState('all');
  const [locations, setLocations] = useState<SimpleSFCLocation[]>([]);
  const {
    data: locationsData,
    error: locationsError,
    isLoading: isLoadingLocations
  } = useGetAllLocationsQuery({ data: { entries: 1000, orderBy: { field: 'title', order: 'asc' } } }, { skip: !isSuperAdmin });

  const { addToast } = useToast();

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading instructors', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (locationsError && !isLoadingLocations) {
      addToast('Error while loading locations', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsData, locationsError, isLoadingLocations]);

  useEffect(() => {
    if (locationsData && locationsData.results) {
      setLocations(locationsData.results);
    }
  }, [locationsData]);

  const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(event.target.value);
  };

  const handleLocationChange = (event: React.ChangeEvent<any>) => {
    setLocation(event.target.value);
  };

  const onFilter = () => {
    if (toDate < fromDate) {
      addToast('End date must be after the start date', 'error');
      return;
    }
    const filters = [
      {
        operator: 'greater_equal_than',
        field: 'date',
        value: dateToUTC(dateFromString(fromDate, 'yyyy-MM-dd'))
      },
      {
        operator: 'lesser_equal_than',
        field: 'date',
        value: dateToUTC(dateFromString(toDate, 'yyyy-MM-dd'))
      },
      ...(isAdminUser && location !== 'all'
        ? [
            {
              operator: 'equals',
              field: 'location_id',
              value: location
            }
          ]
        : []),
      ...(selectedInstructor[0]?.id
        ? [
            {
              operator: 'equals',
              field: 'instructed_id',
              value: String(selectedInstructor[0].id)
            }
          ]
        : [])
    ];
    onApplyFilters(filters);
  };

  return (
    <div>
      <Row className="align-items-end mb-3">
        <Col
          xs={6}
          md={4}
          lg={3}
          xl={2}
          className="mb-1 pe-1"
        >
          <Form.Group controlId="formDateFrom">
            <Form.Label>From</Form.Label>
            <Form.Control
              type="date"
              name="from"
              value={fromDate}
              onChange={handleFromDateChange}
              style={{ padding: '7px 10px' }}
              disabled={disabled}
            />
          </Form.Group>
        </Col>

        <Col
          xs={6}
          md={4}
          lg={3}
          xl={2}
          className="px-lg-1 mb-1 px-2"
        >
          <Form.Group controlId="formDateTo">
            <Form.Label>To</Form.Label>
            <Form.Control
              type="date"
              name="to"
              value={toDate}
              onChange={handleToDateChange}
              style={{ padding: '7px 10px' }}
              disabled={disabled}
            />
          </Form.Group>
        </Col>

        {isAdminUser && (
          <Col
            xs={8}
            lg={3}
            xl={2}
            className="px-lg-1 mb-1 pe-2"
          >
            <Form.Group controlId="instructor">
              <Form.Label>Instructor</Form.Label>
              <Typeahead
                id="calendar-instructor"
                labelKey="label"
                options={instructorOptions}
                selected={selectedInstructor}
                onChange={(selected) =>
                  setSelectedInstructor((selected as InstructorOption[]) ?? [])
                }
                placeholder="All"
                clearButton
                minLength={0}
                disabled={disabled || isLoading}
                emptyLabel="No instructors found"
                flip
                positionFixed
                inputProps={{ style: { padding: '7px 10px' } }}
              />
            </Form.Group>
          </Col>
        )}
        {isSuperAdmin && (
          <Col
            xs={8}
            lg={3}
            xl={2}
            className="px-lg-1 mb-1 pe-2"
          >
            <Form.Group controlId="location">
              <Form.Label>Location</Form.Label>
              <Form.Control
                as="select"
                name="location"
                value={location}
                onChange={handleLocationChange}
                style={{ padding: '7px 10px' }}
                disabled={disabled}
              >
                <option value="all">All</option>
                {locations.map((location) => (
                  <option
                    key={location?.id}
                    value={location?.id}
                  >
                    {location?.title}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        )}

        <Col
          xs={4}
          md="auto"
          className="ps-md-1 mb-1 mt-3 ps-2"
        >
          <Button
            type="button"
            variant="outline-primary"
            onClick={() => onFilter()}
            className="d-flex align-items-center text-uppercase"
            disabled={disabled || loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Loading...
              </>
            ) : (
              <>
                <HiOutlineAdjustmentsHorizontal
                  size={20}
                  className="me-2"
                />
                Submit
              </>
            )}
          </Button>
        </Col>
      </Row>
    </div>
  );
};
